/** Curated recipes + helpers to turn live plugin metadata into insertable prompt snippets. */

export interface PluginUsageField {
  name: string;
  type?: string;
  required?: boolean;
  description?: string | null;
}

export interface BuilderPlugin {
  name: string;
  display_name?: string;
  description?: string;
  category?: string;
  tags?: string[];
  required_params?: string[];
  optional_params?: string[];
  usage_info?: {
    inputs?: PluginUsageField[];
    output?: { type?: string; object_name?: string | null; fields?: string[]; example?: any };
    summary?: string;
    example?: { input?: any; output?: any } | null;
  };
}

export interface CatalogItem {
  id: string;
  title: string;
  hint: string;
  tags: string[];
  insertMarkdown: string;
}

/** Generic building blocks shown first. Everything else still appears below. */
export const COMMON_PLUGIN_NAMES: string[] = [
  'csv_to_json',
  'json_to_csv',
  'combine_arrays',
  'filter_array',
  'sort_array',
  'group_by',
  'transform_json',
  'json_to_markdown_table',
  'markdown_to_html',
  'scrape_url',
  'fetch_url',
  'html_to_file',
  'submit_llm_batch',
  'check_batch_status',
  'send_composed_email',
  'extract_text',
  'stitch_video_clips',
  'resize_image',
];

const SPECIALIZED_NAME_RE = /mealplan|meal_plan|replacement|ingest|autotag|grocery|passio|label_option|food_concern|product_image|wrap_as_days|day_multipliers/i;

export type PluginGroup = 'common' | 'general' | 'specialized';

export function pluginGroup(plugin: BuilderPlugin): PluginGroup {
  if (COMMON_PLUGIN_NAMES.includes(plugin.name)) return 'common';
  const blob = `${plugin.name} ${plugin.display_name || ''} ${plugin.description || ''}`;
  if (SPECIALIZED_NAME_RE.test(blob)) return 'specialized';
  return 'general';
}

export function sortPluginsForBuilder(plugins: BuilderPlugin[]): BuilderPlugin[] {
  return [...plugins].sort((a, b) => {
    const ga = pluginGroup(a);
    const gb = pluginGroup(b);
    const order = { common: 0, general: 1, specialized: 2 };
    if (order[ga] !== order[gb]) return order[ga] - order[gb];
    if (ga === 'common') {
      return COMMON_PLUGIN_NAMES.indexOf(a.name) - COMMON_PLUGIN_NAMES.indexOf(b.name);
    }
    return (a.display_name || a.name).localeCompare(b.display_name || b.name);
  });
}

export const PLUGIN_CATEGORY_LABELS: Record<string, string> = {
  file_conversion: 'File conversion',
  data_transformation: 'Data',
  api_integration: 'APIs & batch',
  video_processing: 'Video',
  audio_processing: 'Audio',
  image_processing: 'Images',
  communication: 'Email & comms',
  utility: 'Utilities',
};

/** Extra wiring notes for plugins that are easy to misuse. Every plugin still gets an auto entry. */
const PLUGIN_WIRING_HINTS: Record<string, string> = {
  csv_to_json:
    'A later AI mapping step must receive only {{THIS.columns}} and {{THIS.sample}} — never {{THIS.data}} (that is the full file). Prefer parse_numbers: false and include_row_numbers: true.',
  submit_llm_batch:
    'Does not wait. Pair with plugin:check_batch_status as the NEXT / exit step. This step\'s AI Model is the batch model (GPT → OpenAI Batch, Gemini → Vertex). Claude cannot batch. Do not mapOverArray this step.',
  submit_autotag_batch:
    'Does not wait. Pair with plugin:check_batch_status. This step\'s AI Model is the batch model.',
  check_batch_status:
    'Poll-only. Re-run / Check now. No model. When done, download 01_CLEAN_RESULTS.json (records.json) and 00_RUN_SUMMARY.json.',
  combine_arrays:
    'Use after a mapOverArray step to merge results. {"arrays": "{{PREV}}", "flatten": true}. Do not use transform_json to extract arrays.',
  transform_json:
    'Key remapping only. jsonpath does nothing. Prefer combine_arrays to flatten mapped output.',
  scrape_url:
    'Forwards pages, all_images, og_image. Later HTML must use real URLs from {{THIS.all_images}}, never placeholder image hosts.',
  html_to_file:
    'Uploads HTML and returns html_url. Forward real image URLs into the HTML step; never invent placeholder srcs.',
};

export function categoryLabel(category?: string): string {
  if (!category) return 'Other';
  return PLUGIN_CATEGORY_LABELS[category] || category.replace(/_/g, ' ');
}

export function pluginOutputFields(plugin: BuilderPlugin): string[] {
  const fields = plugin.usage_info?.output?.fields;
  if (Array.isArray(fields) && fields.length) return fields.map(String);
  const example = plugin.usage_info?.output?.example ?? plugin.usage_info?.example?.output;
  if (example && typeof example === 'object' && !Array.isArray(example)) return Object.keys(example);
  return [];
}

export function pluginInputs(plugin: BuilderPlugin): PluginUsageField[] {
  const fromUsage = plugin.usage_info?.inputs;
  if (Array.isArray(fromUsage) && fromUsage.length) return fromUsage;
  const req = plugin.required_params || [];
  const opt = plugin.optional_params || [];
  return [
    ...req.map((name) => ({ name, required: true })),
    ...opt.map((name) => ({ name, required: false })),
  ];
}

function typicalInputTemplate(plugin: BuilderPlugin): string {
  const inputs = pluginInputs(plugin);
  const required = inputs.filter((i) => i.required);
  const names = (required.length ? required : inputs.slice(0, 4)).map((i) => i.name);
  if (!names.length) return '{\n  "input": "{{input.text}}"\n}';
  const lines = names.map((n) => {
    if (n === 'csv_text') return `  "${n}": "{{input.source_text}}"`;
    if (n === 'html' || n === 'html_content') return `  "${n}": "{{PREV.html_content}}"`;
    if (n === 'url' || n === 'website_url') return `  "${n}": "{{input.website_url}}"`;
    if (n === 'items') return `  "${n}": "{{PREV.data}}"`;
    if (n === 'batch_job_name') return `  "${n}": "{{PREV.batch_job_name}}"`;
    if (n === 'output_uri') return `  "${n}": "{{PREV.output_uri}}"`;
    if (n === 'arrays') return `  "${n}": "{{PREV}}"`;
    return `  "${n}": "{{input.${n}}}"`;
  });
  if (plugin.name === 'csv_to_json') {
    lines.push('  "parse_numbers": false');
    lines.push('  "include_row_numbers": true');
  }
  return `{\n${lines.join(',\n')}\n}`;
}

export function pluginToInsertMarkdown(plugin: BuilderPlugin): string {
  const id = `plugin:${plugin.name}`;
  const title = plugin.display_name || plugin.name;
  const desc = (plugin.description || plugin.usage_info?.summary || '').trim();
  const inputs = pluginInputs(plugin);
  const outputs = pluginOutputFields(plugin);
  const req = inputs.filter((i) => i.required).map((i) => i.name);
  const opt = inputs.filter((i) => !i.required).map((i) => i.name);
  const hint = PLUGIN_WIRING_HINTS[plugin.name];
  const lines = [
    `### ${id} — ${title}`,
    '',
    desc || `Code plugin \`${id}\`.`,
    '',
    `Use as a plugin step (existing_id "${id}"), not an AI function.`,
    'Do not hard-code a node number here — Clean up prompt will assign node-1, node-2, …',
  ];
  if (req.length) lines.push('', `Required inputs: ${req.join(', ')}`);
  if (opt.length) lines.push(`Optional inputs: ${opt.join(', ')}`);
  if (outputs.length) {
    lines.push('', `Outputs (reference as {{THIS.field}} until cleanup numbers nodes): ${outputs.join(', ')}`);
  }
  lines.push('', 'Typical inputTemplate:', typicalInputTemplate(plugin));
  if (hint) lines.push('', `Wiring: ${hint}`);
  lines.push('', 'Placeholders: THIS = this step, PREV = the step before it, NEXT = the step after it.');
  return lines.join('\n');
}

export const BUILDER_RECIPES: CatalogItem[] = [
  {
    id: 'recipe-csv-map',
    title: 'CSV → infer mapping (sample only)',
    hint: 'Parse CSV, then AI-map using headers + 5-row sample — never the full file.',
    tags: ['csv', 'sample', 'columns', 'mapping'],
    insertMarkdown: `### Recipe: CSV parse then infer mapping

THIS STEP: plugin:csv_to_json
inputTemplate:
{
  "csv_text": "{{input.source_text}}",
  "parse_numbers": false,
  "include_row_numbers": true
}
Outputs: data, sample, columns, rows

NEXT STEP: AI function — infer source → target mapping. mapOverArray: false.
inputTemplate MUST be:
{
  "columns": "{{THIS.columns}}",
  "sample": "{{THIS.sample}}"
}
Do NOT pass {{THIS.data}} into that mapping step.

Later per-row steps mapOverArray over {{THIS.data}} (or submit_llm_batch with items: "{{THIS.data}}").

Placeholders THIS / NEXT: click Clean up prompt to assign node-1, node-2, …`,
  },
  {
    id: 'recipe-map-over-array',
    title: 'Live mapOverArray over parsed rows',
    hint: 'One AI call per row in the parsed data array. For small interactive runs, not thousands of rows.',
    tags: ['mapOverArray', 'data', 'live'],
    insertMarkdown: `### Recipe: live mapOverArray

On the per-item AI step (after a parse step that returns data):
- mapOverArray: true
- arrayPath: PREV.data  (the full parsed rows, not sample)
- continueOnItemError: true
- inputTemplate uses {{item.field}} for the current row; add {{PREV}} if a shared mapping object lives on the previous step
- Do not use gpt-5-mini on live mapOverArray
- Array items must be objects with named fields, not plain strings

Placeholders PREV: click Clean up prompt to assign real node-N ids.`,
  },
  {
    id: 'recipe-batch',
    title: 'LLM batch submit + status',
    hint: 'Fire-and-forget batch for large CSV/row jobs. GPT → OpenAI Batch, Gemini → Vertex.',
    tags: ['batch', 'submit_llm_batch', 'check_batch_status'],
    insertMarkdown: `### Recipe: LLM batch (submit then poll)

THIS STEP: plugin:submit_llm_batch  (does not wait; do not mapOverArray)
Batch Model / allowed_models = the model that actually runs (e.g. gpt-5.4-mini). Claude cannot batch.
inputTemplate example:
{
  "items": "{{PREV.data}}",
  "mapping": "{{PREV}}",
  "prompt_id": "uc2_row_transform",
  "max_tokens": 8192,
  "reasoning_effort": "low"
}
If mapping is a different earlier step than the row array, say so in your notes; Clean up prompt will wire them.

NEXT STEP (exit): plugin:check_batch_status
inputTemplate:
{
  "batch_job_name": "{{THIS.batch_job_name}}",
  "output_uri": "{{THIS.output_uri}}"
}
Re-run / Check now to poll. Download 01_CLEAN_RESULTS.json when done.

Placeholders THIS / PREV / NEXT: click Clean up prompt to assign node-1, node-2, …`,
  },
  {
    id: 'recipe-upload',
    title: 'Runtime upload (paste or file)',
    hint: 'One execute-dialog control: dropzone + optional paste. Prefer this over textarea + file.',
    tags: ['upload', 'runtime', 'csv', 'pdf'],
    insertMarkdown: `### RUNTIME INPUTS
[
  {
    "key": "source_text",
    "label": "Source file",
    "type": "upload",
    "required": true,
    "placeholder": "Drop a file or paste text",
    "accept": ".csv,.txt",
    "paste": true,
    "max_files": 1
  }
]
First node reads {{input.source_text}}. For PDFs/images add file_key: "source_file_url" and put those types in accept.
Do not emit both a textarea and a separate file field for the same source.`,
  },
  {
    id: 'recipe-scrape-images',
    title: 'Scrape URL → real image URLs',
    hint: 'Use scrape_url.all_images in later HTML. Never placeholder image hosts.',
    tags: ['scrape_url', 'all_images', 'html'],
    insertMarkdown: `### Recipe: scrape then use images

THIS STEP: plugin:scrape_url with the page URL from {{input.website_url}}
Outputs include pages, all_images, og_image.

Later HTML / html_to_file steps MUST receive {{THIS.all_images}} (and any generated image URLs).
Every <img src> must be a real URL from that pool. Placeholder hosts are forbidden.

Placeholders THIS: click Clean up prompt to assign node-1, node-2, …`,
  },
  {
    id: 'recipe-vision-then-map',
    title: 'Vision/extract once, then mapOverArray',
    hint: 'One extract call on a PDF/image/JSON, then iterate extracted data — not one call per file unless asked.',
    tags: ['vision', 'pdf', 'extract', 'mapOverArray'],
    insertMarkdown: `### Recipe: extract media/document, then map products

THIS STEP: AI vision/extract (Gemini flash) on the uploaded file. Return { data, sample, columns, rows } with open objects (do not drop extra source keys).
mapOverArray is over extracted products in data, not over files.

NEXT STEP: infer mapping from columns + sample only (same as CSV recipe).
THEN: mapOverArray over the extract step's data (not sample).

Runtime upload accept should include the file types (e.g. .pdf,.png,.jpg,.json,.txt,.xlsx) with max_files: 1 unless the spec says otherwise.

Placeholders THIS / NEXT: click Clean up prompt to assign node-1, node-2, …`,
  },
  {
    id: 'recipe-combine',
    title: 'Combine mapped arrays',
    hint: 'After mapOverArray, flatten with combine_arrays. Skip transform_json.',
    tags: ['combine_arrays', 'mapOverArray'],
    insertMarkdown: `### Recipe: combine mapped output

After a mapOverArray step, use plugin:combine_arrays:
{
  "arrays": "{{PREV}}",
  "flatten": true
}
If each mapped item has an inner array field, combine_arrays extracts and flattens it. Do not add transform_json just to unwrap.

Placeholders PREV: click Clean up prompt to assign real node-N ids.`,
  },
];

export const BUILDER_MECHANICS: CatalogItem[] = [
  {
    id: 'mech-placeholders',
    title: 'How to pass data between steps',
    hint: '{{input.x}} from the run dialog, {{PREV.field}} from the previous step, {{item.x}} when looping.',
    tags: ['inputTemplate', 'node', 'item', 'PREV', 'THIS'],
    insertMarkdown: `### How to pass data between steps

In a step's inputTemplate, pull values like this:

- {{input.source_text}} — what the user typed or uploaded when they run the chain. The name after "input." is the runtime field key.
- {{PREV.data}} — a field from the previous step. PREV = the step before this one.
- {{THIS.columns}} — a field this step itself outputs (rare in inputs; used when describing the next step).
- {{item.sku}} — the current row when this step loops over a list (mapOverArray).

While drafting you can write THIS / PREV / NEXT instead of guessing node-1, node-2. Click **Clean up prompt** when you are done inserting — it numbers the steps and connects them so data actually flows.

Do not write step-1. The ids are node-1, node-2, …`,
  },
  {
    id: 'mech-runtime-types',
    title: 'Run dialog fields (runtime inputs)',
    hint: 'The boxes the user fills when they click Run: text, upload, url, etc.',
    tags: ['runtime_inputs', 'upload'],
    insertMarkdown: `### RUNTIME INPUTS
The execute dialog shows one control per item. Prefer type "upload" when they may paste OR drop a file.

[
  {
    "key": "source_text",
    "label": "Source file",
    "type": "upload",
    "required": true,
    "placeholder": "Drop a file or paste text",
    "accept": ".csv,.txt",
    "paste": true,
    "max_files": 1
  }
]

Other types: text, textarea, url, number, boolean, select.
The first step reads the value as {{input.source_text}} — the name after input. must match "key".`,
  },
  {
    id: 'mech-edges',
    title: 'Step order (what runs after what)',
    hint: 'If step B needs step A\'s output, say so. Otherwise B may run too early and get empty data.',
    tags: ['order', 'exit', 'edges'],
    insertMarkdown: `### Step order

Number steps node-1, node-2, node-3 in the order they should run.

If a later step uses an earlier step's output, say that clearly, e.g.:
- node-3 inputTemplate uses {{node-1.columns}} and {{node-2}}
- or while drafting: THIS / PREV / NEXT

**Clean up prompt** turns that into connections. If you skip it, a step can run before its data exists and look empty.

The **last** step (no step after it) is what the chain returns. For an LLM batch, that last step is check_batch_status (the poll), not submit.

If one step needs TWO earlier steps (parsed rows AND the mapping), mention both.`,
  },
  {
    id: 'mech-verbatim',
    title: 'Keep my AI prompt exactly as written',
    hint: 'Stops the chain generator from rewriting a system prompt you already wrote.',
    tags: ['verbatim', 'instructions'],
    insertMarkdown: `Use the following as this function's instructions verbatim. Do not rewrite, summarize, or improve them.

\`\`\`
(paste the real system prompt here)
\`\`\``,
  },
  {
    id: 'mech-optional',
    title: 'Optional step (do not fail the chain)',
    hint: 'If this step errors, log it and keep going.',
    tags: ['optional'],
    insertMarkdown: `This step is optional: if it fails, log a warning and continue the chain. Title it with (OPTIONAL) or set optional: true.`,
  },
  {
    id: 'mech-google-search',
    title: 'Live web search on a Gemini step',
    hint: 'Gemini only. Ask the model not to dump [1] citation marks into the output.',
    tags: ['google', 'search', 'gemini'],
    insertMarkdown: `Enable Google Search on this Gemini step (enable_google_search: true). Instruct the model: do not include bracketed citation numbers like [1] in the output; weave facts into prose.`,
  },
  {
    id: 'mech-constraints',
    title: 'Hard rules for the generator',
    hint: 'Paste at the end of your spec so the builder does not add extra steps or plugins.',
    tags: ['constraints', 'planner'],
    insertMarkdown: `## Builder constraints (do not violate)
- Exactly N steps (node-1 … node-N). The last step is the chain output.
- No extra steps, web search, or plugins unless listed above.
- Do not invent plugin names. Use plugin:<name> from the catalog.
- Instruction blocks above are copied into the AI functions unchanged.
- Mapping AI steps that follow csv_to_json use columns + sample only, never the full file.`,
  },
];

export function searchCatalogItems(items: CatalogItem[], query: string): CatalogItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  return items.filter((item) => {
    const blob = `${item.title} ${item.hint} ${item.tags.join(' ')} ${item.insertMarkdown}`.toLowerCase();
    return blob.includes(q);
  });
}

export function searchPlugins(plugins: BuilderPlugin[], query: string): BuilderPlugin[] {
  const q = query.trim().toLowerCase();
  if (!q) return plugins;
  return plugins.filter((p) => {
    const fields = pluginOutputFields(p).join(' ');
    const blob = [
      p.name,
      p.display_name,
      p.description,
      p.category,
      (p.tags || []).join(' '),
      fields,
      p.usage_info?.summary,
    ].join(' ').toLowerCase();
    return blob.includes(q);
  });
}
